import type { ReactNode } from 'react'
import type { RichTextBlock, RichTextInline } from '@/domain/article'

const inlineNode = (node: RichTextInline, key: string): ReactNode => {
  if (node.type === 'link') {
    return (
      <a key={key} href={node.url} target="_blank" rel="noreferrer">
        {node.children.map((child, index) => inlineNode(child, `${key}-${index}`))}
      </a>
    )
  }

  let content: ReactNode = node.text

  if (node.code) content = <code>{content}</code>
  if (node.bold) content = <strong>{content}</strong>
  if (node.italic) content = <em>{content}</em>
  if (node.underline) content = <span className="article-prose__underline">{content}</span>
  if (node.strikethrough) content = <s>{content}</s>

  return <span key={key}>{content}</span>
}

const inlineChildren = (children: RichTextInline[]) =>
  children.map((child, index) => inlineNode(child, String(index)))

export const RichTextRenderer = ({
  blocks,
  headingIds = {},
  className = 'article-prose',
}: {
  blocks: RichTextBlock[]
  headingIds?: Record<number, string>
  className?: string
}) => (
  <div className={className}>
    {blocks.map((block, index) => {
      const key = `${block.type}-${index}`

      if (block.type === 'heading') {
        const id = headingIds[index]
        if (block.level === 2) return <h2 key={key} id={id}>{inlineChildren(block.children)}</h2>
        if (block.level === 3) return <h3 key={key} id={id}>{inlineChildren(block.children)}</h3>
        if (block.level === 4) return <h4 key={key} id={id}>{inlineChildren(block.children)}</h4>
        return <h2 key={key} id={id}>{inlineChildren(block.children)}</h2>
      }

      if (block.type === 'list') {
        const ListTag = block.format === 'ordered' ? 'ol' : 'ul'
        return (
          <ListTag key={key}>
            {block.children.map((item, itemIndex) => (
              <li key={`${key}-${itemIndex}`}>{inlineChildren(item.children)}</li>
            ))}
          </ListTag>
        )
      }

      if (block.type === 'quote') {
        return <blockquote key={key}>{inlineChildren(block.children)}</blockquote>
      }

      if (block.type === 'code') {
        return <pre key={key}>{inlineChildren(block.children)}</pre>
      }

      if (block.type === 'image') {
        return (
          <figure key={key}>
            <img
              src={block.image.url}
              alt={block.image.alternativeText ?? ''}
              width={block.image.width}
              height={block.image.height}
              loading="lazy"
              decoding="async"
            />
            {block.children?.length ? <figcaption>{inlineChildren(block.children)}</figcaption> : null}
          </figure>
        )
      }

      if (block.type === 'separator') {
        return <hr key={key} className="article-separator" />
      }

      return <p key={key}>{inlineChildren(block.children)}</p>
    })}
  </div>
)
